import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  CategoryModel,
  CategorySearchRequest,
  CategorySearchResponse,
  CreateCategoryRequest,
  PartnerCategoryIdsRequest,
} from '@app/core/models/product-parameters/category.model';

@Injectable({
  providedIn: 'root',
})
export class CategoryService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/categories`;
  }

  private get partnerBaseUrl(): string {
    return `${this.apiBaseUrl}/b2b/admin/partners`;
  }

  search(
    page = 0,
    size = 10,
    body: CategorySearchRequest = {}
  ): Observable<CategorySearchResponse> {
    this.isLoading.set(true);

    const params = new HttpParams().set('page', page.toString()).set('size', size.toString());

    return this.http.post<CategorySearchResponse>(`${this.baseUrl}/search`, body, { params }).pipe(
      catchError(error => {
        console.error('Ошибка при получении категорий:', error);
        this.translate.get('Ошибка при получении категорий').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of({
          currentPageNumber: 0,
          pageSize: size,
          totalElements: 0,
          totalPages: 0,
          isLastPage: true,
          isFirstPage: true,
          isEmpty: true,
          content: [],
        });
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  getPage(page = 0, size = 10): Observable<CategorySearchResponse> {
    this.isLoading.set(true);
    const params = new HttpParams().set('page', page.toString()).set('size', size.toString());

    return this.http.get<CategorySearchResponse>(this.baseUrl, { params }).pipe(
      catchError(error => {
        console.error('Ошибка при получении категорий (GET):', error);
        this.translate.get('Ошибка при получении категорий').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of({
          currentPageNumber: 0,
          pageSize: size,
          totalElements: 0,
          totalPages: 0,
          isLastPage: true,
          isFirstPage: true,
          isEmpty: true,
          content: [],
        });
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  create(body: CreateCategoryRequest): Observable<CategoryModel> {
    this.isLoading.set(true);
    return this.http.post<CategoryModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании категории:', error);
        this.translate.get('Ошибка при создании категории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(id: string, body: CreateCategoryRequest): Observable<CategoryModel> {
    this.isLoading.set(true);
    return this.http.put<CategoryModel>(`${this.baseUrl}/${id}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении категории:', error);
        this.translate.get('Ошибка при обновлении категории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(id: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении категории:', error);
        this.translate.get('Ошибка при удалении категории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  activate(id: string): Observable<CategoryModel> {
    this.isLoading.set(true);
    return this.http.patch<CategoryModel>(`${this.baseUrl}/${id}/activate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при активации категории:', error);
        this.translate.get('Ошибка при активации категории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deactivate(id: string): Observable<CategoryModel> {
    this.isLoading.set(true);
    return this.http.patch<CategoryModel>(`${this.baseUrl}/${id}/deactivate`, {}).pipe(
      catchError(error => {
        console.error('Ошибка при деактивации категории:', error);
        this.translate.get('Ошибка при деактивации категории').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  getByPartnerId(partnerId: string): Observable<CategoryModel[]> {
    if (!partnerId) return of([]);
    this.isLoading.set(true);
    return this.http.get<CategoryModel[]>(`${this.partnerBaseUrl}/${partnerId}/categories`).pipe(
      catchError(error => {
        console.error('Ошибка при получении категорий партнера:', error);
        this.translate.get('Ошибка при получении категорий').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  addToPartner(partnerId: string, categoryIds: string[]): Observable<CategoryModel[]> {
    this.isLoading.set(true);
    const body: PartnerCategoryIdsRequest = { categoryIds };
    return this.http
      .post<CategoryModel[]>(`${this.partnerBaseUrl}/${partnerId}/categories`, body)
      .pipe(
        catchError(error => {
          console.error('Ошибка при добавлении категорий партнеру:', error);
          this.translate.get('Ошибка при создании категории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          throw error;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  replaceForPartner(partnerId: string, categoryIds: string[]): Observable<CategoryModel[]> {
    this.isLoading.set(true);
    const body: PartnerCategoryIdsRequest = { categoryIds };
    return this.http
      .put<CategoryModel[]>(`${this.partnerBaseUrl}/${partnerId}/categories`, body)
      .pipe(
        catchError(error => {
          console.error('Ошибка при обновлении категорий партнера:', error);
          this.translate.get('Ошибка при обновлении категории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          throw error;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  removeFromPartner(partnerId: string, categoryId: string): Observable<CategoryModel> {
    this.isLoading.set(true);
    return this.http
      .delete<CategoryModel>(`${this.partnerBaseUrl}/${partnerId}/categories/${categoryId}`)
      .pipe(
        catchError(error => {
          console.error('Ошибка при удалении категории у партнера:', error);
          this.translate.get('Ошибка при удалении категории').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          throw error;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }
}
